import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Eye, Edit, Sparkles, Wand2, RotateCcw } from 'lucide-react';

/**
 * Markdown Editor Component with Preview and AI features
 * Supports Expand, Rewrite, and Improve operations
 */
const MarkdownEditor = ({
  value,
  onChange,
  onExpand,
  onRewrite,
  onImprove,
  chapterTitle,
  loading = false,
  placeholder = 'Start writing your chapter content...'
}) => {
  const [view, setView] = useState('split'); // 'edit', 'preview', 'split'
  const [showAiMenu, setShowAiMenu] = useState(false);

  return (
    <div className="flex flex-col h-full border border-gray-300 rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-300">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setView('edit')}
            className={`p-2 rounded ${view === 'edit' ? 'bg-primary-100 text-primary-700' : 'hover:bg-gray-200'}`}
            title="Edit Mode"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => setView('preview')}
            className={`p-2 rounded ${view === 'preview' ? 'bg-primary-100 text-primary-700' : 'hover:bg-gray-200'}`}
            title="Preview Mode"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={() => setView('split')}
            className={`p-2 rounded ${view === 'split' ? 'bg-primary-100 text-primary-700' : 'hover:bg-gray-200'}`}
            title="Split View"
          >
            <div className="w-4 h-4 border-2 border-current rounded-sm" />
          </button>
        </div>

        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">
            {value.split(/\s+/).filter(w => w).length} words
          </span>

          {/* AI Menu */}
          <div className="relative">
            <button
              onClick={() => setShowAiMenu(!showAiMenu)}
              className="flex items-center space-x-1 px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              disabled={loading || !value}
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              <span className="text-sm font-medium">AI</span>
            </button>

            {showAiMenu && !loading && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                <button
                  onClick={() => {
                    onExpand?.();
                    setShowAiMenu(false);
                  }}
                  className="flex items-center w-full px-4 py-2 text-left hover:bg-gray-50"
                >
                  <Wand2 className="w-4 h-4 mr-2 text-primary-600" />
                  <span className="text-sm">Expand Content</span>
                </button>
                <button
                  onClick={() => {
                    onRewrite?.();
                    setShowAiMenu(false);
                  }}
                  className="flex items-center w-full px-4 py-2 text-left hover:bg-gray-50"
                >
                  <RotateCcw className="w-4 h-4 mr-2 text-primary-600" />
                  <span className="text-sm">Rewrite</span>
                </button>
                <button
                  onClick={() => {
                    onImprove?.();
                    setShowAiMenu(false);
                  }}
                  className="flex items-center w-full px-4 py-2 text-left hover:bg-gray-50"
                >
                  <Sparkles className="w-4 h-4 mr-2 text-primary-600" />
                  <span className="text-sm">Improve</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Editor Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Edit Pane */}
        {(view === 'edit' || view === 'split') && (
          <div className={view === 'split' ? 'w-1/2 border-r border-gray-300' : 'w-full'}>
            <textarea
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
              className="w-full h-full p-4 resize-none font-mono text-sm focus:outline-none"
            />
          </div>
        )}

        {/* Preview Pane */}
        {(view === 'preview' || view === 'split') && (
          <div className={view === 'split' ? 'w-1/2' : 'w-full'} style={{
            backgroundColor: 'white'
          }}>
            <div className="w-full h-full p-6 overflow-auto prose max-w-none">
              {value ? (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({ node, ...props }) => <h1 className="text-2xl font-bold mt-6 mb-4" {...props} />,
                    h2: ({ node, ...props }) => <h2 className="text-xl font-bold mt-5 mb-3" {...props} />,
                    h3: ({ node, ...props }) => <h3 className="text-lg font-semibold mt-4 mb-2" {...props} />,
                    p: ({ node, ...props }) => <p className="my-3 text-gray-800 leading-relaxed" {...props} />,
                    strong: ({ node, ...props }) => <strong className="font-bold text-gray-900" {...props} />,
                    em: ({ node, ...props }) => <em className="italic" {...props} />,
                    ul: ({ node, ...props }) => <ul className="list-disc pl-6 my-3" {...props} />,
                    ol: ({ node, ...props }) => <ol className="list-decimal pl-6 my-3" {...props} />,
                    li: ({ node, ...props }) => <li className="my-1" {...props} />,
                  }}
                >
                  {value}
                </ReactMarkdown>
              ) : (
                <p className="text-gray-400 italic">Preview will appear here...</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Markdown Hint */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-300 text-xs text-gray-500">
        <strong>Tip:</strong> Use **bold**, *italic*, # headings, - lists, and > quotes
      </div>
    </div>
  );
};

export default MarkdownEditor;